import React, { useState } from "react"
import {
	Grid, Link as LinkMUI, Typography, IconButton
} from "@material-ui/core"

import {
	AccessibleDrawer, ActionDialog, Profile
} from "@/components"

import {
	WarningTwoTone as WarningIcon,
	Close as CloseIcon,
	CardGiftcard as CardGiftcardIcon
} from "@material-ui/icons"

import { useChatGlobalStateStore } from "@/store/ChatGlobalState"
import { useReferAndEarn } from "@/hooks/useReferAndEarn"

import { isSmallScreen } from "@/utils/checkDevice"

import Input from "@/pages/Attendance/Chat/ConversationPanel/Input"
import ConversationHeader from "@/pages/Attendance/Chat/ConversationPanel/ConversationHeader"
import ClientInfo from "@/pages/Attendance/Chat/ConversationPanel/ClientInfo"
import MessageList from "@/pages/Attendance/Chat/ConversationPanel/MessageList"
import MessageProcessingStepsDialog from "@/pages/Attendance/Chat/ConversationPanel/MessageProcessingStepsDialog"

import AlertContainer from "@/components/AlertContainer"
import WABAChatCommunicationPermissionBar from "@/@integrations/WABA/components/Chat/WABAChatCommunicationPermissionBar"
import MetaFreeEntryPointWindowBar from "@/@integrations/WABA/components/Chat/MetaFreeEntryPointWindowBar"

import useStyles from "@/pages/Attendance/Chat/ConversationPanel/styles"
import { colors } from "@/styles/colors"

import { isAbleToValidateEmail } from "@/utils/time"

const ConversationPanel: React.FC = () => {
	const classes = useStyles()
	const chatGlobalStateStore = useChatGlobalStateStore()

	const canValidateEmail = isAbleToValidateEmail()
	const { isReferAndEarnAlertVisible: showReferAndEarnAlert, hideAlert: hideReferAndEarnAlert, openReferAndEarnPage } = useReferAndEarn()

	const isReferAndEarnBannerDisabled = true

	const [openClientInfoDrawer, setOpenClientInfoDrawer] = useState(!isSmallScreen)
	const [openValidateEmailDialog, setOpenValidateEmailDialog] = useState(false)

	const handleCloseChat = () => {
		chatGlobalStateStore.chat.close()
	}
	const hadleOpenDialog = () => {
		setOpenValidateEmailDialog(true)
	}

	const hadleCloseDialog = () => {
		setOpenValidateEmailDialog(false)
	}

	const handleOpenClientInfoDrawer = () => {
		setOpenClientInfoDrawer(true)
	}

	const handleCloseClientInfoDrawer = () => {
		setOpenClientInfoDrawer(false)
	}

	return (
		<>
			<AccessibleDrawer
				id="conversation-panel-drawer"
				variant={isSmallScreen ? chatGlobalStateStore.chat.current ? "persistent" : "temporary" : "permanent"}
				open={Boolean(chatGlobalStateStore.chat.current)}
				anchor="right"
				onClose={handleCloseChat}
				onMobileBackButtonPress={handleCloseChat}
				className={classes.chatDrawer}
				classes={{
					paper: classes.chatDrawerPaper
				}}
			>

				{!isReferAndEarnBannerDisabled && showReferAndEarnAlert && (
					<Grid container justifyContent="space-between" alignItems="flex-start" className={classes.alertContainer}>
						<Grid item xs>
							<AlertContainer
								title="INDIQUE E GANHE"
								alertType="info"
								icon={<CardGiftcardIcon htmlColor={colors.purple[600]} fontSize="medium" />}
								customButton={
									<IconButton size="small" onClick={hideReferAndEarnAlert}>
										<CloseIcon fontSize="small" />
									</IconButton>
								}
							>
								<Grid container direction="row" alignItems="center">
									<Grid item xs>
										<Typography variant="body2">
													Indique seus amigos e ganhe benefícios exclusivos quando eles se cadastrarem!{" "}
											<LinkMUI
												underline="always"
												onClick={openReferAndEarnPage}
												style={{
													textDecoration: "none",
													cursor: "pointer"
												}}
											>
														Clique aqui
											</LinkMUI>
										</Typography>
									</Grid>
								</Grid>
							</AlertContainer>
						</Grid>
					</Grid>
				)}

				{canValidateEmail && (
					<Grid container justifyContent="space-between" alignItems="flex-start" className={classes.alertContainer}>
						<Grid item xs>
							<AlertContainer
								title="ATENÇÃO"
								alertType="warning"
								icon={<WarningIcon htmlColor={colors.yellow[500]} fontSize="medium" />}
							>
								<Typography variant="body2">
								Seu e-mail não está verificado. Ajude a proteger sua conta contra o acesso não autorizado.{" "}
									<LinkMUI
										underline="always"
										onClick={hadleOpenDialog}
										style={{
											textDecoration: "none",
											cursor: "pointer"
										}}
									>
									Validar email
									</LinkMUI>
								</Typography>
							</AlertContainer>
						</Grid>
					</Grid>
				)}

				<ActionDialog
					title={""}
					fullWidth
					maxWidth="md"
					onClose={hadleCloseDialog}
					openDialog={openValidateEmailDialog}
				>
					<Profile />
				</ActionDialog>
				<Grid
					container
					alignItems="center"
					justify="center"
					direction="column"
					className={classes.chatRightSideContainer}
				>
					<Grid
						container
						className={classes.chatHeader}
						justify="flex-start"
						alignItems="center"
						wrap="nowrap"
					>
						<ConversationHeader
							onClose={handleCloseChat}
							onClick={handleOpenClientInfoDrawer}
						/>
					</Grid>

					{chatGlobalStateStore.chat.current?.channelType === "waba" && (
						<MetaFreeEntryPointWindowBar
							status={chatGlobalStateStore.chat.current?.wabaChannelChatFreeEntryPointWindow?.status ?? "paused"}
							scheduledPauseAt={chatGlobalStateStore.chat.current?.wabaChannelChatFreeEntryPointWindow?.scheduledPauseAt}
						/>
					)}

					<Grid
						container
						className={classes.fixedHeightContainer}
					>
						<MessageList />
					</Grid>

					<MessageProcessingStepsDialog
						messageType={chatGlobalStateStore.message.processingStepsDialog.current?.type}
						processingSteps={chatGlobalStateStore.message.processingStepsDialog.current?.inboxChannelChatMessageLog?.processingSteps || {}}
						opened={chatGlobalStateStore.message.processingStepsDialog.isOpened}
						onClose={() => chatGlobalStateStore.message.processingStepsDialog.close()}
					/>

					<Grid
						container
						direction="column"
					>
						{chatGlobalStateStore.chat.current?.channelType === "waba" && (
							<WABAChatCommunicationPermissionBar
								status={chatGlobalStateStore.chat.current?.wabaChannelChatCommunicationPermission?.status ?? "paused"}
								scheduledPauseAt={chatGlobalStateStore.chat.current?.wabaChannelChatCommunicationPermission?.scheduledPauseAt}
							/>
						)}

						<Grid
							container
							alignItems="center"
							spacing={1}
						>
							<Grid item xs>
								<Input />
							</Grid>
						</Grid>
					</Grid>
				</Grid>
			</AccessibleDrawer>

			{chatGlobalStateStore.client.current && (
				<ClientInfo
					open={openClientInfoDrawer}
					onClose={handleCloseClientInfoDrawer}
					clientId={chatGlobalStateStore.client.current.id}
				/>
			)}
		</>
	)
}

export default ConversationPanel
